import { cn } from "@/lib/utils";
import { at } from "./motion";

/** [x, y, width, height] in Figma px. */
type Rect = [number, number, number, number];

export interface SelectionGeometry {
  /** Font size of the highlighted text in Figma px; every rect is converted to em of it. */
  font: number;
  /** The box, relative to the highlighted word's glyph box (its inline content area). */
  box: Rect;
  /** Handles (bars and squares), relative to the box. */
  handles: Rect[];
  /**
   * Width of the English word's glyph box, in Figma px at `font`. With it the box keeps its Figma
   * margins around whatever word is rendered (a translation, an edit) instead of its Figma width,
   * and the right-hand handles follow the box's right edge.
   */
  word?: number;
}

const em = (px: number, font: number) => `${px / font}em`;
const place = ([x, y, w, h]: Rect, font: number) => ({
  left: em(x, font),
  top: em(y, font),
  width: em(w, font),
  height: em(h, font),
});
/** Like `place`, but pinned to the right edge of a container `width` Figma px wide. */
const placeRight = ([x, y, w, h]: Rect, width: number, font: number) => ({
  right: em(width - x - w, font),
  top: em(y, font),
  width: em(w, font),
  height: em(h, font),
});

function Box({
  geometry,
  fill,
  handle,
  hatch,
  delay,
  caret,
  className,
}: {
  geometry: SelectionGeometry;
  fill: string;
  handle: string;
  hatch: string;
  delay: number;
  caret?: boolean;
  className: string;
}) {
  const { font, box, handles, word } = geometry;
  const [x, y, w, h] = box;
  // Fitted to the word: both sides pinned, so the box grows or shrinks with the rendered text.
  const boxStyle =
    word === undefined
      ? place(box, font)
      : { left: em(x, font), right: em(word - x - w, font), top: em(y, font), height: em(h, font) };
  return (
    <span aria-hidden="true" className={cn("pointer-events-none absolute", className)} style={boxStyle}>
      {/* inside a live reveal scope the tint paints across, the left handles drop in just ahead of
          it and the right ones land as the paint arrives */}
      <span className="tc-rv-paint absolute inset-0" style={{ background: fill, ...at(delay) }} />
      <span
        className="tc-rv-paint absolute inset-0 bg-[length:1.25rem_1.25rem] bg-left-top opacity-[0.09]"
        style={{ backgroundImage: `url('${hatch}')`, ...at(delay) }}
      />
      {handles.map((rect, i) => (
        <span
          key={i}
          // with `caret`, the right-hand bar keeps blinking like a text cursor
          className={cn("tc-rv-drop absolute", caret && rect[0] >= w / 2 && rect[3] > rect[2] * 4 && "tc-caret")}
          style={{
            ...(word !== undefined && rect[0] >= w / 2 ? placeRight(rect, w, font) : place(rect, font)),
            background: handle,
            ...at(rect[0] < w / 2 ? delay - 0.1 : delay + 0.6),
          }}
        />
      ))}
    </span>
  );
}

/**
 * The Figma "selection" drawn around a highlighted word: a tinted, hatched box with a bar and a
 * square handle on each side. Render it inside a `relative` inline span that wraps the word; the
 * span should have `line-height: 0` so the serif face does not grow the line box.
 */
export default function SelectionHighlight({
  mobile,
  desktop,
  fill,
  handle,
  mobileFill = fill,
  hatch = "/trading-competition/highlight-hatch.png",
  mobileHatch = "/trading-competition/highlight-hatch-mobile.png",
  delay = 0.45,
  caret = false,
}: {
  mobile: SelectionGeometry;
  desktop: SelectionGeometry;
  fill: string;
  handle: string;
  mobileFill?: string;
  hatch?: string;
  mobileHatch?: string;
  /** Seconds after its reveal scope goes live (the title's rise takes ~0.9s). */
  delay?: number;
  /** Keep the right bar blinking once it lands, like a text caret. */
  caret?: boolean;
}) {
  return (
    <>
      <Box
        geometry={mobile}
        fill={mobileFill}
        handle={handle}
        hatch={mobileHatch}
        delay={delay}
        caret={caret}
        className="lg:hidden"
      />
      <Box
        geometry={desktop}
        fill={fill}
        handle={handle}
        hatch={hatch}
        delay={delay}
        caret={caret}
        className="hidden lg:block"
      />
    </>
  );
}
